import React, { useState, useEffect, useCallback } from 'react';
import { 
  Shield, 
  Key, 
  Bell, 
  Trash2, 
  Eye, 
  EyeOff, 
  Save,
  AlertTriangle
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase'; // Import your supabase client
import SharedNavigation from './SharedNavigation';
import '../styles/AccountSettings.css';

const AccountSettings = () => {
  const { user, logout } = useAuth();
  const [activeSection, setActiveSection] = useState('security');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Form states
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true
  });

  // Wrap createDefaultPreferences in useCallback first
  const createDefaultPreferences = useCallback(async () => {
    try {
      const { error } = await supabase
        .from('user_preferences')
        .insert([{
          user_id: user.id,
          notification_settings: notificationSettings
        }]);

      if (error) throw error;
    } catch (error) {
      console.error('Error creating default preferences:', error);
    }
  }, [user, notificationSettings]);

  // Wrap loadUserPreferences in useCallback to stabilize the reference
  const loadUserPreferences = useCallback(async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('user_preferences')
        .select('notification_settings')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 is "not found" error
        throw error;
      }

      if (data && data.notification_settings) {
        setNotificationSettings(data.notification_settings);
      }
    } catch (error) {
      console.error('Error loading user preferences:', error);
      // If no preferences exist, create default ones
      await createDefaultPreferences();
    }
  }, [user, createDefaultPreferences]);

  // Load user preferences from Supabase on component mount
  useEffect(() => {
    if (user) {
      loadUserPreferences();
    }
  }, [user, loadUserPreferences]);

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNotificationChange = (setting) => {
    setNotificationSettings(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }
    
    if (passwordForm.newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters long' });
      return;
    }

    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // First verify current password by attempting to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: passwordForm.currentPassword
      });

      if (signInError) {
        throw new Error('Current password is incorrect');
      }

      // Update password using Supabase auth
      const { error: updateError } = await supabase.auth.updateUser({
        password: passwordForm.newPassword
      });

      if (updateError) {
        throw updateError;
      }

      setMessage({ type: 'success', text: 'Password updated successfully' });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);

    } catch (error) {
      console.error('Password update error:', error);
      setMessage({ 
        type: 'error', 
        text: error.message || 'Failed to update password. Please check your current password.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Update or insert user preferences
      const { error } = await supabase
        .from('user_preferences')
        .upsert([{
          user_id: user.id,
          notification_settings: notificationSettings,
          updated_at: new Date().toISOString()
        }], {
          onConflict: 'user_id'
        });

      if (error) throw error;

      setMessage({ type: 'success', text: 'Settings saved successfully' });
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);

    } catch (error) {
      console.error('Settings save error:', error);
      setMessage({ type: 'error', text: 'Failed to save settings. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

const handleDeleteAccount = async () => {
  setIsLoading(true);
  setMessage({ type: '', text: '' });

  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      throw new Error('No valid session found');
    }

    console.log('Attempting to delete account for user:', user.id);

const serverUrl = process.env.REACT_APP_SERVER_URL || 'https://sports-scheduler-backend.vercel.app';
const apiUrl = `${serverUrl}/api/user/delete-account`;
    
    console.log('Making request to:', apiUrl); // Debug log

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      credentials: 'include', // Add this for CORS
      body: JSON.stringify({ 
        userId: user.id 
      })
    });

    console.log('Response status:', response.status);
     console.log('Response headers:', [...response.headers.entries()]);
    console.log('Response ok:', response.ok);

    // Check if response is ok
       if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.details || errorMessage;
          console.error('Server error details:', errorData);
        } else {
          const textResponse = await response.text();
          console.log('Non-JSON response:', textResponse);
          if (textResponse) {
            errorMessage = textResponse;
          }
        }
      } catch (parseError) {
        console.warn('Could not parse error response:', parseError);
      }
      
      throw new Error(errorMessage);
    }

    // Parse response
    const responseText = await response.text();
    console.log('Raw response:', responseText);
    
    let responseData = {};
    if (responseText) {
      try {
        responseData = JSON.parse(responseText);
      } catch (parseError) {
        console.warn('Response was not valid JSON:', responseText);
        responseData = { message: 'Account deletion completed' };
      }
    }

    console.log('Account deleted successfully:', responseData.message);
    
    // Sign out the user after successful deletion
    await supabase.auth.signOut();
    await logout();
    
    setMessage({ 
      type: 'success', 
      text: 'Account deleted successfully. You have been signed out.' 
    });

  } catch (error) {
    console.error('Account deletion error:', error);
    let errorMessage = error.message;
    if (error.message.includes('fetch')) {
      errorMessage = 'Network error: Could not reach the server. Please check your connection.';
    } else if (error.message.includes('CORS')) {
      errorMessage = 'Connection error: Please try again or contact support.';
    }
    
    setMessage({ 
      type: 'error', 
      text: `Account deletion failed: ${errorMessage}` 
    });
  } finally {
    setIsLoading(false);
    setShowDeleteModal(false);
  }
};

  const sections = [
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'danger', label: 'Danger Zone', icon: AlertTriangle }
  ];

  return (
    <div className="account-settings-container">
      <SharedNavigation />
      
      <div className="account-settings-content">
        <div className="account-settings-header">
          <h1>Account Settings</h1>
          <p>Manage your account security, notifications, and privacy preferences</p>
        </div>

        <div className="account-settings-layout">
          {/* Sidebar */}
          <div className="settings-sidebar">
            <nav className="settings-nav">
              {sections.map(section => {
                const Icon = section.icon;
                return (
                  <button
                    key={section.id}
                    className={`settings-nav-item ${activeSection === section.id ? 'active' : ''}`}
                    onClick={() => setActiveSection(section.id)}
                  >
                    <Icon size={18} />
                    {section.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Main Content */}
          <div className="settings-main">
            {message.text && (
              <div className={`settings-message ${message.type}`}>
                {message.text}
              </div>
            )}

            {/* Security Section */}
            {activeSection === 'security' && (
              <div className="settings-section">
                <div className="section-header">
                  <Shield size={24} />
                  <div>
                    <h2>Security Settings</h2>
                    <p>Manage your password and security preferences</p>
                  </div>
                </div>

                <div className="settings-card">
                  <h3>Change Password</h3>
                  <form onSubmit={handlePasswordSubmit} className="password-form">
                    <div className="form-group">
                      <label htmlFor="currentPassword">Current Password</label>
                      <div className="password-input-group">
                        <input
                          type={showCurrentPassword ? 'text' : 'password'}
                          id="currentPassword"
                          name="currentPassword"
                          value={passwordForm.currentPassword}
                          onChange={handlePasswordChange}
                          required
                        />
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        >
                          {showCurrentPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="newPassword">New Password</label>
                      <div className="password-input-group">
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          id="newPassword"
                          name="newPassword"
                          value={passwordForm.newPassword}
                          onChange={handlePasswordChange}
                          required
                          minLength={8}
                        />
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                        >
                          {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="confirmPassword">Confirm New Password</label>
                      <div className="password-input-group">
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          id="confirmPassword"
                          name="confirmPassword"
                          value={passwordForm.confirmPassword}
                          onChange={handlePasswordChange}
                          required
                        />
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>

                    <button type="submit" className="save-password-btn" disabled={isLoading}>
                      <Key size={16} />
                      {isLoading ? 'Updating...' : 'Update Password'}
                    </button>
                  </form>
                </div>
              </div>
            )}

            {/* Notifications Section */}
            {activeSection === 'notifications' && (
              <div className="settings-section">
                <div className="section-header">
                  <Bell size={24} />
                  <div>
                    <h2>Notification Preferences</h2>
                    <p>Choose what notifications you want to receive</p>
                  </div>
                </div>

                <div className="settings-card">
                  <div className="notification-settings">
                    <div className="setting-item">
                      <div className="setting-info">
                        <h4>Email Notifications</h4>
                        <p>Receive notifications via email</p>
                      </div>
                      <div className="setting-control">
                        <label className="toggle-switch">
                          <input
                            type="checkbox"
                            checked={notificationSettings.emailNotifications}
                            onChange={() => handleNotificationChange('emailNotifications')}
                          />
                          <span className="toggle-slider"></span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <button className="save-settings-btn" onClick={handleSaveSettings} disabled={isLoading}>
                    <Save size={16} />
                    {isLoading ? 'Saving...' : 'Save Preferences'}
                  </button>
                </div>
              </div>
            )}

            {/* Danger Zone Section */}
            {activeSection === 'danger' && (
              <div className="settings-section">
                <div className="section-header">
                  <AlertTriangle size={24} />
                  <div>
                    <h2>Danger Zone</h2>
                    <p>Irreversible and destructive actions</p>
                  </div>
                </div>

                <div className="settings-card danger-zone">
                  <div className="danger-item">
                    <div className="danger-info">
                      <h4>Delete Account</h4>
                      <p>Permanently delete your account and all associated data. This action cannot be undone.</p>
                    </div>
                    <button 
                      className="danger-btn"
                      onClick={() => setShowDeleteModal(true)}
                    >
                      <Trash2 size={16} />
                      Delete Account
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="delete-modal">
            <div className="modal-header">
              <AlertTriangle size={24} className="warning-icon" />
              <h3>Delete Account</h3>
            </div>
            <div className="modal-body">
              <p>Are you absolutely sure you want to delete your account?</p>
              <p>This will permanently delete your account and remove all associated data from our servers. This action cannot be undone.</p>
              {user && (
                <div className="account-info">
                  <p><strong>Account:</strong> {user.email}</p>
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button 
                className="cancel-btn"
                onClick={() => setShowDeleteModal(false)}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button 
                className="delete-confirm-btn"
                onClick={handleDeleteAccount}
                disabled={isLoading}
              >
                {isLoading ? 'Deleting...' : 'Yes, Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSettings;